/// <reference path="../../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// Presentation-only Appearance pane. All values are controlled props
// so the host (apps/web/src/shell/hosts/SettingsModalHost.tsx and the
// desktop equivalent) can route changes through its preferences store
// AND flip document.documentElement attributes so the rest of the
// shell re-renders in the new theme.
//
// Pinned to Figma:
//
//   * Display preference — node 3542:4631. Section title 16/22.4
//     Lato Bold #F5F6F8, description 12/16.8 Lato Regular #B5BAC3.
//     Three theme cards (200×164 wrapper, preview 200×132 rounded 10
//     with a 1px active border #537EFF, label 14/20 below).
//   * Accent colors    — node 3542:4870 / 3641:7476. 24×24 swatches
//     with 9px corner radius and 12px gap (Figma's "Color Selector"
//     row).
//   * Chat background  — node 3542:4856. Single inline pill row.
//   * Text size        — node 3542:5074. Five size pills, gap 8.
//
// Tailwind utilities that don't compile cleanly through NativeWind
// (notably the gradient previews and the multi-stop "Gradient" chat
// background card) fall back to inline `style` props with raw hex
// values so RN-macos sees the same paint as RN-Web.

import { Check, Monitor, Moon, Sun } from "lucide-react-native";
import { Pressable, Text, View } from "react-native";
import { LinearGradient } from "../../components/LinearGradient";
import { PillButton } from "../controls";
import { SettingsDivider, SettingsRow } from "../SettingsRow";
import {
	type MessageLayoutMode,
	useUiPreferencesStore,
} from "../../preferences/uiPreferencesStore";
import {
	ACCENT_PALETTE,
	DEFAULT_ACCENT_ID,
} from "../../preferences/accentPalette";

const MESSAGE_LAYOUT_OPTIONS: ReadonlyArray<{
	readonly value: MessageLayoutMode;
	readonly label: string;
}> = [
	{ value: "bubble", label: "Bubbles" },
	{ value: "stacked", label: "Compact" },
];

export type AppearanceTheme = "light" | "dark" | "system";
export type AppearanceBackground = "plain" | "subtle" | "gradient";

const THEME_OPTIONS: ReadonlyArray<{
	readonly value: AppearanceTheme;
	readonly label: string;
	readonly icon: typeof Monitor;
}> = [
	{ value: "system", label: "System preference", icon: Monitor },
	{ value: "light", label: "Light theme", icon: Sun },
	{ value: "dark", label: "Dark theme", icon: Moon },
];

const BACKGROUND_OPTIONS: ReadonlyArray<{
	readonly value: AppearanceBackground;
	readonly label: string;
}> = [
	{ value: "plain", label: "Plain" },
	{ value: "subtle", label: "Subtle" },
	{ value: "gradient", label: "Gradient" },
];

// Accent swatches come from the shared `accentPalette` (single source of
// truth with the web PreferencesStore, which maps the selected id →
// `--ping-accent`). The first swatch (`default`) reproduces the shipped
// brand colour and clears the runtime override when selected.

export interface AppearancePaneProps {
	readonly theme: AppearanceTheme;
	readonly onThemeChange: (next: AppearanceTheme) => void;
	readonly accent: string;
	readonly onAccentChange: (next: string) => void;
	readonly background: AppearanceBackground;
	readonly onBackgroundChange: (next: AppearanceBackground) => void;
	readonly fontScale: number;
	readonly onFontScaleChange: (next: number) => void;
}

export function AppearancePane(props: AppearancePaneProps): JSX.Element {
	const {
		theme,
		onThemeChange,
		accent,
		onAccentChange,
		background,
		onBackgroundChange,
		fontScale,
		onFontScaleChange,
	} = props;

	// Message layout density (bubble vs stacked/compact). Read/write the
	// shared UI-preferences store directly so the choice is honoured by
	// `ChatRoom` on both web and desktop without extra host wiring.
	const messageLayout = useUiPreferencesStore((s) => s.messageLayout);
	const setMessageLayout = useUiPreferencesStore((s) => s.setMessageLayout);

	return (
		// Container — Figma 3542:4627: vertical gap 16.
		<View className="flex flex-col gap-4">
			<SettingsRow
				variant="hero"
				title="Appearance"
				description="Change how the dashboard looks and feels."
				testID="settings-appearance-intro"
			/>

			<SettingsRow
				title="Display preference"
				description="Switch between light and dark modes."
				testID="settings-appearance-theme"
			>
				{/*
					Theme cards — Figma 3542:4635: horizontal gap 20. Each
					card 200×164 (200×132 preview + 12 gap + 20 label).
				*/}
				<View className="flex flex-row gap-5">
					{THEME_OPTIONS.map((opt) => {
						const isActive = theme === opt.value;
						return (
							<Pressable
								key={opt.value}
								accessibilityRole="radio"
								accessibilityState={{ selected: isActive }}
								onPress={() => onThemeChange(opt.value)}
								className="flex flex-col gap-3"
							>
								<View
									// Preview card — 200×132, radius 10. Active
									// card gets a 3px brand-blue stroke (Figma
									// node 3601:8172 strokeWeight 3). Inactive
									// cards get a 1px hairline so the surface is
									// visible against either modal bg.
									className="
										w-[200px] h-[132px] rounded-[10px]
										overflow-hidden
									"
									style={{
										borderColor: isActive ? "#537EFF" : "#22242A",
										borderWidth: isActive ? 3 : 1,
									}}
								>
									{opt.value === "system" ? (
										// System card: anti-diagonal split between
										// the light + dark surface colors at exactly
										// 50% — hard stop emulates Figma's clip-path
										// divide. `react-native-linear-gradient`
										// renders natively on macOS; on web the
										// aliased CSS-gradient shim resolves the
										// same `linear-gradient(...)` directive.
										<LinearGradient
											colors={["#11131A", "#11131A", "#F5F6F8", "#F5F6F8"]}
											locations={[0, 0.5, 0.5, 1]}
											start={{ x: 0, y: 0 }}
											end={{ x: 1, y: 1 }}
											style={{ flex: 1 }}
										>
											<ThemePreview value={opt.value} />
										</LinearGradient>
									) : (
										<View
											className="flex-1"
											style={{ backgroundColor: themePreviewBg(opt.value) }}
										>
											<ThemePreview value={opt.value} />
										</View>
									)}
								</View>
								<View className="flex flex-row items-center gap-2">
									<View
										className="
											w-4 h-4 rounded-full
											border
											flex items-center justify-center
										"
										style={{
											borderColor: isActive ? "#537EFF" : "#3A3D45",
											backgroundColor: isActive ? "#537EFF" : "transparent",
										}}
									>
										{isActive && (
											<Check size={10} strokeWidth={3} color="#FFFFFF" />
										)}
									</View>
									<Text className="text-[14px] leading-[20px] font-normal text-light-text-primary dark:text-[#F5F6F8]">
										{opt.label}
									</Text>
								</View>
							</Pressable>
						);
					})}
				</View>
			</SettingsRow>

			<SettingsDivider />

			<SettingsRow
				title="Chat background"
				description="Backdrop drawn behind chat threads."
				testID="settings-appearance-background"
			>
				<View className="flex flex-row gap-3 flex-wrap">
					{BACKGROUND_OPTIONS.map((opt) => {
						const isActive = background === opt.value;
						return (
							<Pressable
								key={opt.value}
								accessibilityRole="radio"
								accessibilityState={{ selected: isActive }}
								onPress={() => onBackgroundChange(opt.value)}
								// Pill row — 36 tall, padding 16h, radius 12,
								// fill #15171C border #22242A (matches the
								// TextField surface). Active variant tints the
								// fill with the brand blue @ 12% and switches
								// the border. Light token classes flip the
								// inactive surface in light mode.
								className={`
									flex flex-row items-center gap-2
									h-9 px-4
									rounded-xl border
									${
										isActive
											? ""
											: "bg-light-surface-secondary dark:bg-[#15171C] border-light-border-secondary dark:border-[#22242A]"
									}
								`}
								style={
									isActive
										? {
												backgroundColor: "rgba(83,126,255,0.12)",
												borderColor: "#537EFF",
											}
										: undefined
								}
							>
								<Text
									className={`text-[14px] leading-[20px] font-medium ${
										isActive
											? "text-light-text-primary dark:text-[#F5F6F8]"
											: "text-light-text-secondary dark:text-[#B5BAC3]"
									}`}
								>
									{opt.label}
								</Text>
							</Pressable>
						);
					})}
				</View>
			</SettingsRow>

			<SettingsDivider />

			<SettingsRow
				title="Message layout"
				description="Bubbles wrap each message; Compact stacks them under one avatar."
				testID="settings-appearance-layout"
			>
				{/* Bubble vs Stacked/Compact — Figma 2804:25342 "Bubble VS
				    Flat". Two pills mapped to the shared UI preference. */}
				<View className="flex flex-row items-center gap-2">
					{MESSAGE_LAYOUT_OPTIONS.map((opt) => (
						<PillButton
							key={opt.value}
							variant={messageLayout === opt.value ? "primary" : "secondary"}
							onClick={() => setMessageLayout(opt.value)}
						>
							{opt.label}
						</PillButton>
					))}
				</View>
			</SettingsRow>

			<SettingsDivider />

			<SettingsRow
				title="Accent color"
				description="Used for selection, links, and primary actions."
				testID="settings-appearance-accent"
			>
				{/*
					Color picker — Figma 3641:7476: horizontal, gap 16.
					Eight 24×24 swatches with corner radius 9.
				*/}
				<View className="flex flex-row flex-wrap gap-4">
					{ACCENT_PALETTE.map((color) => {
						const isActive = accent === color.id;
						return (
							<Pressable
								key={color.id}
								accessibilityRole="button"
								accessibilityLabel={color.id}
								accessibilityState={{ selected: isActive }}
								onPress={() => onAccentChange(color.id)}
								className="
									w-6 h-6
									rounded-[9px]
									border-2
									flex items-center justify-center
								"
								style={{
									backgroundColor: color.hex,
									// White halo on the selected swatch — matches
									// the Figma component's "selected" variant.
									borderColor: isActive ? "#FFFFFF" : color.hex,
								}}
							>
								{isActive && (
									<Check size={12} strokeWidth={3} color="#FFFFFF" />
								)}
							</Pressable>
						);
					})}
				</View>
			</SettingsRow>

			<SettingsDivider />

			<SettingsRow
				title="Zoom scale"
				description="Scale the entire interface up or down."
				testID="settings-appearance-zoom"
			>
				<View className="flex flex-col gap-3 max-w-[480px]">
					<View className="flex flex-row items-center gap-2">
						{[0.85, 0.95, 1.0, 1.15, 1.3].map((scale) => {
							const isActive = Math.abs(fontScale - scale) < 0.001;
							return (
								<PillButton
									key={scale}
									variant={isActive ? "primary" : "secondary"}
									onClick={() => onFontScaleChange(scale)}
								>
									{`${Math.round(scale * 100)}%`}
								</PillButton>
							);
						})}
					</View>
					{/* No inline preview: the whole UI (this modal included) zooms
					    live the moment a scale is picked, so a locally-scaled sample
					    would double-apply. The active pill is the live readout. */}
					<Text className="text-[12px] leading-[16px] font-normal text-light-text-secondary dark:text-[#B5BAC3]">
						{`Interface is at ${Math.round(fontScale * 100)}% — everything scales together.`}
					</Text>
				</View>
			</SettingsRow>

			<SettingsDivider />

			<SettingsRow title="Reset" description="Restore the default appearance.">
				<View className="flex flex-row">
					<PillButton
						variant="secondary"
						onClick={() => {
							onThemeChange("system");
							onBackgroundChange("plain");
							onAccentChange(DEFAULT_ACCENT_ID);
							onFontScaleChange(1.0);
						}}
					>
						Reset to defaults
					</PillButton>
				</View>
			</SettingsRow>
		</View>
	);
}

// Background hex for the light + dark theme preview cards. The
// "system" card is rendered separately above via `<LinearGradient>`
// with a hard 50% split between dark (#11131A) and light (#F5F6F8)
// along the top-left → bottom-right diagonal, so it's never reached
// through this helper. Kept narrow on purpose.
function themePreviewBg(theme: "light" | "dark"): string {
	return theme === "light" ? "#F5F6F8" : "#11131A";
}

interface ThemePreviewProps {
	readonly value: AppearanceTheme;
}

// Small in-card scene that hints at the look of each mode. Three
// rounded "message bubbles" stacked on the matching surface — enough
// to read at 200×132 in the card. Inline styles because the gradient
// /split preview doesn't translate to a Tailwind utility.
function ThemePreview(props: ThemePreviewProps): JSX.Element {
	const { value } = props;
	const isLight = value === "light";
	const bubbleA = isLight ? "#E5E7EB" : "#1E2128";
	const bubbleB = isLight ? "#FFFFFF" : "#272A30";
	const accentBubble = "#537EFF";

	return (
		<View className="w-full h-full p-4 flex flex-col justify-end gap-2">
			<View
				className="self-start rounded-[10px] h-3 w-[60%]"
				style={{ backgroundColor: bubbleA }}
			/>
			<View
				className="self-start rounded-[10px] h-3 w-[40%]"
				style={{ backgroundColor: bubbleB }}
			/>
			<View
				className="self-end rounded-[10px] h-3 w-[50%]"
				style={{ backgroundColor: accentBubble }}
			/>
		</View>
	);
}
